import { useState, useEffect } from 'react';
import { useUrl } from 'crossroad';
import { useManuscript } from '@/context/manuscript-context';
import type { InteractionConstraint } from '@/context/manuscript-context';

interface UseGuidedStudyCapabilityHandlerProps {
    passageId: string;
    currentStep: GuidedStudyStep | undefined;
    currentStepIndex: number;
    currentSubStepIndex: number;
    totalSubSteps: number;
    markRequirementMet: (stepIndex: number, subStepIndex: number) => void;
}

export function useGuidedStudyCapabilityHandler({
    passageId,
    currentStep,
    currentStepIndex,
    currentSubStepIndex,
    totalSubSteps,
    markRequirementMet,
}: UseGuidedStudyCapabilityHandlerProps) {
    const [, setUrl] = useUrl();
    const [showCapabilityModal, setShowCapabilityModal] = useState(false);
    const [showFollowupPrompt, setShowFollowupPrompt] = useState(false);
    const [hasViewedCapability, setHasViewedCapability] = useState(false);
    const [activeCapabilityRef, setActiveCapabilityRef] = useState<string | null>(null);
    const [activeCapabilityItems, setActiveCapabilityItems] = useState<CapabilityItemReference[] | null>(null);

    const {
        setActiveTab,
        setDrawModeStyleId,
        setInteractionConstraint,
        setManuallyOpened,
    } = useManuscript();

    // Reset followup prompt when step or substep changes
    // This ensures that prompts don't "leak" to the next step
    useEffect(() => {
        setShowFollowupPrompt(false);
        setHasViewedCapability(false);
    }, [currentStepIndex, currentSubStepIndex]);

    const handleCapabilityAction = (itemTitle?: string, overrideRef?: string, overrideItems?: CapabilityItemReference[]) => {
        if (!currentStep) {
            console.warn('handleCapabilityAction called without currentStep');
            return;
        }

        const ref = overrideRef || currentStep.capabilityRef || '';
        const items = overrideItems || currentStep.capabilityItems || null;

        if (!ref) {
            console.warn('No capability reference found');
            return;
        }

        // Set the active capability so the modal/map knows what to show
        setActiveCapabilityRef(ref);
        setActiveCapabilityItems(items || null);

        const isMapAction = ref.startsWith('geographical-context');
        const isTimelineAction = ref.startsWith('historical-context:timeline');
        const isOutlineAction = ref.startsWith('literary-context:outline');

        // Mark as viewed to allow proceeding for this sub-step
        setHasViewedCapability(true);
        markRequirementMet(currentStepIndex, currentSubStepIndex);

        // ONLY on the last sub-step of the whole step
        if ((isMapAction || isTimelineAction || isOutlineAction) && currentSubStepIndex === totalSubSteps - 1) {
            // Followup prompt logic removed for now as it's not being consumed
        }

        // For geographical context, navigate to the map with the place highlighted
        if (isMapAction) {
            const highlight = itemTitle || (items?.[0]?.itemTitle);
            setUrl(`/app/passage/${passageId}/context/places${highlight ? `?highlight=${encodeURIComponent(highlight)}` : ''}`);
        } else if (ref.startsWith('manuscript-study')) {
            // For manuscript study, open the tools and activate draw mode
            setManuallyOpened(true);
            setDrawModeStyleId('yellow-pencil'); // Always activate draw mode for manuscript study

            // Determine which tab to activate based on the capability
            const stepInstruction = (currentStep.instruction || '').toLowerCase();
            if (ref === 'manuscript-study:connections' || stepInstruction.includes('link') || stepInstruction.includes('connect')) {
                setActiveTab('connect');
            } else if (stepInstruction.includes('divide') || stepInstruction.includes('structure')) {
                setActiveTab('structure');
            } else if (ref === 'manuscript-study:notes' || stepInstruction.includes('note')) {
                setActiveTab('note');
            } else if (ref === 'manuscript-study:word-categories' || stepInstruction.includes('use the marking tools')) {
                const constraint: InteractionConstraint = {
                    onlyAllow: ['highlight', 'mark', 'connect'], // Allow connections too
                };

                // Add range restriction only if valid range provided
                if (currentStep.focusRange && currentStep.focusRange.includes('-')) {
                    // Simple heuristic: if it looks like word IDs, use them
                    const parts = currentStep.focusRange.split(' ');
                    if (parts.length === 2 && parts[0].startsWith('w-') && parts[1].startsWith('w-')) {
                        constraint.restrictToRange = { startWordId: parts[0], endWordId: parts[1] };
                    }
                }

                setInteractionConstraint(constraint);
                setActiveTab('highlight');

                // Show followup prompt if it exists (not inside modal)
                if (currentStep.followupPrompt && currentSubStepIndex === totalSubSteps - 1) {
                    setShowFollowupPrompt(true);
                }
            } else {
                // Default for other manuscript study types
                setInteractionConstraint({ onlyAllow: ['highlight', 'structure', 'note'] });
                setActiveTab('highlight');
            }
        } else {
            // For other capabilities, open the modal
            setShowCapabilityModal(true);
        }
    };

    return {
        showCapabilityModal,
        setShowCapabilityModal,
        showFollowupPrompt,
        setShowFollowupPrompt,
        hasViewedCapability,
        setHasViewedCapability,
        activeCapabilityRef,
        activeCapabilityItems,
        handleCapabilityAction,
    };
}
